import Link from 'next/link'
import { useRouter } from 'next/router'

import type { FC, ReactNode } from 'react'
import type { SubNav } from '@/types'

import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { buttonClass } from '@/components/generals/button'
import { SiteSearch } from '@/components/generals/site-search'
import { HUB_NAV } from '@/constants/hubs'

const NAV = [
    ...HUB_NAV.map((group) => ({ href: `/${group.slug}`, label: group.label })),
    { href: '/about', label: 'About' }
]

interface HeaderProps {
    subNav?: SubNav | null
}

// The three category links are plain links, never hover dropdowns. The pillar
// sub-nav is revealed only when the current page belongs to that group, so the
// header stays lightweight everywhere it is not relevant.
export const Header: FC<HeaderProps> = ({ subNav = null }): ReactNode => {
    const { asPath } = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        setOpen(false)
    }, [asPath])

    const path = asPath.split('?')[0].split('#')[0]

    const isActive = (href: string): boolean =>
        href === '/' ? path === '/' : path === href || path.startsWith(`${href}/`)

    const isGroupActive = (href: string): boolean =>
        isActive(href) || (subNav ? href === `/${subNav.groupSlug}` : false)

    return (
        <header className='sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur-md'>
            <Container>
                <div className='flex h-16 items-center justify-between gap-4'>
                    <Link
                        href='/'
                        className='flex items-center gap-2 text-primary'
                        aria-label='WellPath Fusion home'
                    >
                        <Logo width={28} height={28} />
                        <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                            WellPath<span className='text-primary'> Fusion</span>
                        </span>
                    </Link>

                    <nav className='hidden items-center gap-1 md:flex'>
                        {NAV.map((item) => (
                            <Link
                                key={item.href}
                                href={item.href}
                                className={cn(
                                    'rounded-full px-3 py-2 text-sm transition-colors',
                                    isGroupActive(item.href)
                                        ? 'font-medium text-foreground'
                                        : 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='flex items-center gap-2'>
                        <SiteSearch />

                        <Link
                            href='/#subscribe'
                            className={buttonClass({ size: 'sm', className: 'hidden sm:inline-flex' })}
                        >
                            Subscribe
                        </Link>

                        <button
                            type='button'
                            onClick={() => setOpen((value) => !value)}
                            className='inline-flex size-10 items-center justify-center rounded-full text-foreground hover:bg-secondary md:hidden'
                            aria-label='Toggle navigation menu'
                            aria-expanded={open}
                        >
                            {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </div>
                </div>
            </Container>

            {subNav && subNav.pillars.length > 0 && (
                <div className='hidden border-t border-border/60 bg-secondary/40 md:block'>
                    <Container>
                        <nav
                            className='flex items-center gap-1 overflow-x-auto py-2'
                            aria-label={`${subNav.groupName} guides`}
                        >
                            <span className='mr-2 shrink-0 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground'>
                                {subNav.groupName}
                            </span>

                            {subNav.pillars.map((pillar) => (
                                <Link
                                    key={pillar.slug}
                                    href={`/${pillar.slug}`}
                                    aria-current={
                                        isActive(`/${pillar.slug}`) ? 'page' : undefined
                                    }
                                    className={cn(
                                        'shrink-0 rounded-full px-3 py-1.5 text-sm transition-colors',
                                        isActive(`/${pillar.slug}`)
                                            ? 'bg-background font-medium text-foreground shadow-sm'
                                            : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                                    )}
                                >
                                    {pillar.name}
                                </Link>
                            ))}
                        </nav>
                    </Container>
                </div>
            )}

            {open && (
                <div className='border-t border-border/70 bg-background md:hidden'>
                    <Container>
                        <nav className='flex flex-col gap-1 py-3'>
                            {NAV.map((item) => (
                                <div key={item.href}>
                                    <Link
                                        href={item.href}
                                        className={cn(
                                            'block rounded-lg px-3 py-3 text-sm transition-colors',
                                            isGroupActive(item.href)
                                                ? 'bg-secondary font-medium text-foreground'
                                                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                                        )}
                                    >
                                        {item.label}
                                    </Link>

                                    {subNav && item.href === `/${subNav.groupSlug}` && (
                                        <div className='mt-1 flex flex-col gap-0.5 border-l border-border pl-3'>
                                            {subNav.pillars.map((pillar) => (
                                                <Link
                                                    key={pillar.slug}
                                                    href={`/${pillar.slug}`}
                                                    className={cn(
                                                        'rounded-lg px-3 py-2 text-sm transition-colors',
                                                        isActive(`/${pillar.slug}`)
                                                            ? 'font-medium text-foreground'
                                                            : 'text-muted-foreground hover:text-foreground'
                                                    )}
                                                >
                                                    {pillar.name}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            <Link
                                href='/#subscribe'
                                className={buttonClass({ size: 'sm', className: 'mt-2' })}
                            >
                                Subscribe
                            </Link>
                        </nav>
                    </Container>
                </div>
            )}
        </header>
    )
}
